import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Loader2, Search, User, ArrowLeft, Crown, Sparkles, FileText, CreditCard,
  CheckCircle2, Mail, Plus, Flame, Dumbbell, Download, Clock, X, Phone,
  MessageSquare, Target, Undo2, AlertTriangle, Send, MailX, Bell, ChevronDown, ChefHat, Trash2, Eye,
} from 'lucide-react';
import { toast as sonnerToast } from 'sonner';
import {
  StatCard, Section, PaymentsTable, StatusBadge, ManualInvoiceModal, ConfirmMarkPaidModal,
} from './AdminPaymentsPanel';
import { PlanManager, DangerZone, VipNotesEditor, CreditsEditor, SectionCard, Row } from './ClientProfileShared';
import { EmailPreviewModal } from './AdminSiteSettingsPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TEST_EMAIL_RE = /@(bodyuptest\.ch|example\.com|test\.com|fake\.local|anonymized\.local)$|^(test[_@]|e2e_|qa_|promo_|csrf_|client\.demo|timing|comtiming|upsell|stripe_e2e|onboarding_test)/i;
const isTestEmail = (email) => TEST_EMAIL_RE.test((email || '').toLowerCase());

const ProgramDeletionsSection = ({ email, fmtDate }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/program-deletions?email=${encodeURIComponent(email)}`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => setItems(d.deletions || [])).catch(() => setItems([]));
  }, [email]);
  if (!items || items.length === 0) return null;
  return (
    <SectionCard title="Historique suppressions de programmes" icon={Trash2}>
      <div className="space-y-1.5" data-testid="admin-program-deletions">
        {items.map((d, i) => (
          <div key={i} className="flex items-center justify-between gap-2 text-xs py-1.5 border-b border-white/5 last:border-0" data-testid={`program-deletion-row-${i}`}>
            <span className="text-zinc-300 truncate">
              <span className="text-zinc-500">{d.program_type === 'cardio' ? 'Cardio' : 'Musculation'} · </span>
              {d.program_name || '(sans nom)'}
            </span>
            <span className="flex items-center gap-2 shrink-0">
              {d.restored && <span className="text-emerald-400 font-semibold">Restauré</span>}
              <span className="text-zinc-500">{fmtDate(d.deleted_at)}</span>
            </span>
          </div>
        ))}
      </div>
    </SectionCard>
  );
};


/**
 * Onglet "Clients" — fiche client 360° UNIFIÉE (remplace l'ancienne "Recherche client"
 * de Facturation + la liste de l'onglet Accès Client) : infos compte, plan d'accès,
 * profil VIP/tags/notes, historique calculateur, TOUTES les factures/paiements
 * (virement QR-code + Stripe, avec actions complètes + remboursement), messages
 * reçus, leads funnel, zone danger RGPD, et raccourci vers le suivi coaching.
 */
export const AdminClientsPanel = ({ csrfToken, onLogout, fmtDate, onGoToCoaching, initialEmail, onInitialEmailConsumed }) => {
  const [clients, setClients] = useState(null);
  const [q, setQ] = useState('');
  const [prospects, setProspects] = useState([]);
  const [searchingProspects, setSearchingProspects] = useState(false);
  const debounceRef = useRef(null);

  const [selected, setSelected] = useState(null);
  const [profile, setProfile] = useState(null);
  const [vip, setVip] = useState(null);
  const [vipDraft, setVipDraft] = useState(null);
  const [vipSaving, setVipSaving] = useState(false);
  const [vipSaveError, setVipSaveError] = useState(null);
  const [calculations, setCalculations] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [manualInvoiceModal, setManualInvoiceModal] = useState(null);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refunding, setRefunding] = useState(false);
  const [emailFailures, setEmailFailures] = useState([]);
  const [resendingEmail, setResendingEmail] = useState(null);
  const [remindersHistory, setRemindersHistory] = useState([]);
  const [showReminders, setShowReminders] = useState(false);
  const [personalRecipes, setPersonalRecipes] = useState(null);
  const [promoting, setPromoting] = useState(null);
  const [promoteCategory, setPromoteCategory] = useState('plat_chaud');
  const [quickDeleteTarget, setQuickDeleteTarget] = useState(null);
  const [quickDeleting, setQuickDeleting] = useState(false);
  const [testFilterOnly, setTestFilterOnly] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [trash, setTrash] = useState(null);
  const [trashBusyId, setTrashBusyId] = useState(null);
  const [emailPreview, setEmailPreview] = useState(null);
  const [previewingEmail, setPreviewingEmail] = useState(null);


  const fetchWithCredentials = (url) => fetch(url, { credentials: 'include' });
  const fetchWithCsrf = (url, options = {}) => fetch(url, {
    ...options,
    credentials: 'include',
    headers: { ...options.headers, 'X-CSRF-Token': csrfToken, 'Content-Type': 'application/json' },
  });

  useEffect(() => {
    fetchWithCredentials(`${BACKEND_URL}/api/admin/clients`)
      .then((r) => (r.status === 401 ? (onLogout(), Promise.reject()) : r.json()))
      .then((d) => setClients(d.clients || []))
      .catch(() => setClients([]));
    // eslint-disable-next-line
  }, []);

  const loadEmailFailures = useCallback(() => {
    fetchWithCredentials(`${BACKEND_URL}/api/admin/access-email-failures`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setEmailFailures(d.items || []))
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => { loadEmailFailures(); }, [loadEmailFailures]);

  useEffect(() => {
    fetchWithCredentials(`${BACKEND_URL}/api/admin/reminders-history`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setRemindersHistory(d.items || []))
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  const REMINDER_LABEL = { onboarding: ['Onboarding', 'bg-blue-500/20 text-blue-300'], solo_upsell: ['Upsell Suivi Pro+', 'bg-sky-500/20 text-sky-300'], vip_upsell: ['Upsell VIP', 'bg-fuchsia-500/20 text-fuchsia-300'], expiry: ['Fin d\'abonnement', 'bg-amber-500/20 text-amber-300'], meal_plan: ['Plan non ouvert', 'bg-pink-500/20 text-pink-300'] };

  const resendFailedEmail = async (f) => {
    setResendingEmail(f.to_email);
    const endpoint = f.context === 'payment_failed_client' ? 'resend-payment-failed-email' : 'resend-welcome-email';
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/clients/${encodeURIComponent(f.to_email)}/${endpoint}`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      if (data.sent) {
        sonnerToast.success(`Email renvoyé avec succès à ${f.to_email}.`);
        loadEmailFailures();
      } else {
        sonnerToast.error(`Toujours impossible d'envoyer l'email à ${f.to_email} (SMTP indisponible ?). Réessayez plus tard.`);
      }
    } catch (e) {
      sonnerToast.error(e.message || 'Erreur réseau.');
    } finally {
      setResendingEmail(null);
    }
  };

  const previewFailedEmail = async (f) => {
    if (f.context === 'payment_failed_client') { sonnerToast.info("Aperçu disponible uniquement pour l'email de bienvenue."); return; }
    setPreviewingEmail(f.to_email);
    try {
      const res = await fetchWithCredentials(`${BACKEND_URL}/api/admin/clients/welcome-email-preview?plan=${f.plan || ''}&language=fr`);
      const data = await res.json();
      if (!res.ok) throw new Error();
      setEmailPreview(data);
    } catch {
      sonnerToast.error("Impossible de charger l'aperçu.");
    } finally {
      setPreviewingEmail(null);
    }
  };

  const loadDetail = useCallback((email) => {
    setLoadingDetail(true);
    Promise.all([
      fetchWithCredentials(`${BACKEND_URL}/api/admin/billing/clients/profile?email=${encodeURIComponent(email)}`).then((r) => r.json()),
      fetchWithCredentials(`${BACKEND_URL}/api/admin/billing/clients/notes?email=${encodeURIComponent(email)}`).then((r) => r.json()),
      fetchWithCredentials(`${BACKEND_URL}/api/admin/clients/${encodeURIComponent(email)}/calculations`).then((r) => r.json()),
      fetchWithCredentials(`${BACKEND_URL}/api/admin/clients/${encodeURIComponent(email)}/personal-recipes`).then((r) => r.json()),
    ])
      .then(([p, v, c, pr]) => { setProfile(p); setVip(v); setVipDraft(v); setCalculations(c.calculations || []); setPersonalRecipes(pr.recipes || []); })
      .finally(() => setLoadingDetail(false));
  }, []);

  const openClient = (c) => {
    setSelected(c); setProfile(null); setVip(null); setVipDraft(null); setCalculations(null); setPersonalRecipes(null);
    loadDetail(c.email);
  };

  const promoteRecipe = async (recipeId) => {
    setPromoting(recipeId);
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/personal-recipes/${recipeId}/promote`, {
        method: 'POST', body: JSON.stringify({ category: promoteCategory }),
      });
      if (!res.ok) throw new Error();
      sonnerToast.success('Recette ajoutée à la bibliothèque officielle !');
    } catch {
      sonnerToast.error('Impossible de promouvoir cette recette.');
    } finally {
      setPromoting(null);
    }
  };

  // Pré-sélection venant d'un autre onglet (Accès Client, Facturation)
  useEffect(() => {
    if (initialEmail) {
      openClient({ email: initialEmail, name: '' });
      onInitialEmailConsumed && onInitialEmailConsumed();
    }
    // eslint-disable-next-line
  }, [initialEmail]);

  // Recherche globale (prospects sans compte) si aucun résultat local
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const localMatches = (clients || []).filter((c) => (c.email + ' ' + (c.name || '')).toLowerCase().includes(q.toLowerCase()));
    if (q.trim().length < 2 || localMatches.length > 0) { setProspects([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearchingProspects(true);
      try {
        const res = await fetchWithCredentials(`${BACKEND_URL}/api/admin/billing/clients/search?q=${encodeURIComponent(q)}&limit=10`);
        const data = await res.json();
        const localEmails = new Set((clients || []).map((c) => c.email.toLowerCase()));
        setProspects((data.results || []).filter((r) => !localEmails.has(r.email.toLowerCase()) && !r.has_client_account));
      } catch {
        setProspects([]);
      } finally {
        setSearchingProspects(false);
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [q, clients]);

  const refetchProfile = () => loadDetail(selected.email);

  const saveVip = async () => {
    if (!vipDraft) return;
    setVipSaving(true);
    setVipSaveError(null);
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/billing/clients/notes?email=${encodeURIComponent(selected.email)}`, {
        method: 'PUT',
        body: JSON.stringify({
          tags: vipDraft.tags || [], notes: vipDraft.notes || '',
          vip_discount_percent: parseFloat(vipDraft.vip_discount_percent) || 0,
          vip_discount_amount_chf: parseFloat(vipDraft.vip_discount_amount_chf) || 0,
          waive_stripe_fees: !!vipDraft.waive_stripe_fees,
        }),
      });
      if (!res.ok) throw new Error(res.status === 403 ? 'Session expirée, reconnectez-vous' : `Erreur ${res.status}`);
      const updated = await res.json();
      setVip(updated); setVipDraft(updated);
    } catch (err) {
      setVipSaveError(err.message || 'Erreur enregistrement');
    } finally {
      setVipSaving(false);
    }
  };

  const vipDirty = vip && vipDraft && (
    JSON.stringify(vip.tags || []) !== JSON.stringify(vipDraft.tags || []) ||
    (vip.notes || '') !== (vipDraft.notes || '') ||
    Number(vip.vip_discount_percent || 0) !== Number(vipDraft.vip_discount_percent || 0) ||
    Number(vip.vip_discount_amount_chf || 0) !== Number(vipDraft.vip_discount_amount_chf || 0) ||
    !!vip.waive_stripe_fees !== !!vipDraft.waive_stripe_fees
  );

  const markPaid = async (kind, id, note = '') => {
    setActionLoading(true);
    try {
      const endpoint = kind === 'invoice'
        ? `${BACKEND_URL}/api/admin/payments/mark-invoice-paid/${id}`
        : `${BACKEND_URL}/api/admin/payments/mark-session-paid/${id}`;
      const res = await fetchWithCsrf(endpoint, { method: 'POST', body: JSON.stringify({ note }) });
      if (res.status === 401) { onLogout(); return; }
      const json = await res.json();
      if (json.success) {
        sonnerToast.success(json.already_paid ? 'Déjà marqué comme payé.' : `Marqué comme payé. Email envoyé à ${json.email_sent_to}.`);
        refetchProfile();
      } else {
        sonnerToast.error('Erreur lors du marquage.');
      }
    } catch {
      sonnerToast.error('Erreur réseau.');
    } finally {
      setActionLoading(false);
      setConfirmModal(null);
    }
  };

  const sendScheduledNow = async (invoiceNumber) => {
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/payments/send-scheduled-now/${encodeURIComponent(invoiceNumber)}`, { method: 'POST' });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Erreur');
      sonnerToast.success('Facture envoyée immédiatement.');
      refetchProfile();
    } catch (e) {
      sonnerToast.error(e.message || 'Erreur réseau.');
    }
  };

  const doRefund = async () => {
    if (!refundTarget) return;
    setRefunding(true);
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/payments/refund-session/${encodeURIComponent(refundTarget.session_id)}`, { method: 'POST' });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Erreur');
      sonnerToast.success('Paiement remboursé avec succès.');
      refetchProfile();
    } catch (e) {
      sonnerToast.error(e.message || 'Erreur lors du remboursement.');
    } finally {
      setRefunding(false);
      setRefundTarget(null);
    }
  };

  const filtered = (clients || []).filter((c) => {
    if (testFilterOnly && !isTestEmail(c.email)) return false;
    return !q || (c.email + ' ' + (c.name || '')).toLowerCase().includes(q.toLowerCase());
  });
  const testAccountsCount = (clients || []).filter((c) => isTestEmail(c.email)).length;

  const doQuickDelete = async () => {
    if (!quickDeleteTarget?.user_id) return;
    setQuickDeleting(true);
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/clients/by-id/${encodeURIComponent(quickDeleteTarget.user_id)}/soft-delete`, {
        method: 'POST',
        body: JSON.stringify({ reason: 'Nettoyage compte test (suppression rapide admin)' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      sonnerToast.success(`Compte ${quickDeleteTarget.email} envoyé en corbeille (récupérable 30 jours).`);
      setClients((prev) => (prev || []).filter((c) => c.email !== quickDeleteTarget.email));
      setQuickDeleteTarget(null);
    } catch (e) {
      sonnerToast.error(e.message || 'Erreur lors de la suppression.');
    } finally {
      setQuickDeleting(false);
    }
  };

  const loadTrash = useCallback(() => {
    setTrash(null);
    fetchWithCredentials(`${BACKEND_URL}/api/admin/clients/trash`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setTrash(d?.items || []))
      .catch(() => setTrash([]));
    // eslint-disable-next-line
  }, []);

  const openTrash = () => { setShowTrash(true); loadTrash(); };

  const restoreFromTrash = async (item) => {
    setTrashBusyId(item.user_id);
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/clients/by-id/${encodeURIComponent(item.user_id)}/restore`, { method: 'POST' });
      if (!res.ok) throw new Error((await res.json()).detail || 'Erreur');
      sonnerToast.success(`Compte ${item.email} restauré.`);
      setTrash((prev) => (prev || []).filter((t) => t.user_id !== item.user_id));
      setClients(null);
      fetchWithCredentials(`${BACKEND_URL}/api/admin/clients`).then((r) => r.json()).then((d) => setClients(d.clients || []));
    } catch (e) {
      sonnerToast.error(e.message || 'Erreur lors de la restauration.');
    } finally {
      setTrashBusyId(null);
    }
  };

  const purgeFromTrash = async (item) => {
    if (!window.confirm(`Supprimer DÉFINITIVEMENT le compte ${item.email} ? Cette action est irréversible.`)) return;
    setTrashBusyId(item.user_id);
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/clients/by-id/${encodeURIComponent(item.user_id)}/purge`, { method: 'POST' });
      if (!res.ok) throw new Error((await res.json()).detail || 'Erreur');
      sonnerToast.success(`Compte ${item.email} supprimé définitivement.`);
      setTrash((prev) => (prev || []).filter((t) => t.user_id !== item.user_id));
    } catch (e) {
      sonnerToast.error(e.message || 'Erreur lors de la purge.');
    } finally {
      setTrashBusyId(null);
    }
  };

  // ----------------------------- Fiche client détaillée -----------------------------
  if (selected) {
    const data = profile;
    // Badges dérivés en direct de l'abonnement rafraîchi (data.subscription), pas du
    // snapshot pris à l'ouverture de la fiche (selected) — se met à jour immédiatement
    // après un changement de plan sans devoir revenir à la liste et rouvrir la fiche.
    const sub = data?.subscription;
    const subNotExpired = !sub?.current_period_end || new Date(sub.current_period_end) >= new Date();
    const subActive = sub?.status === 'active' && subNotExpired;
    const livePremium = data ? (subActive && ['monthly', 'yearly', 'transformation', 'vip'].includes(sub?.plan)) : selected.premium;
    const liveTransformation = data ? (subActive && ['transformation', 'vip'].includes(sub?.plan)) : selected.transformation;
    return (
      <div data-testid="admin-client-detail" className="space-y-6">
        <button onClick={() => setSelected(null)} className="flex items-center gap-2 text-zinc-400 hover:text-primary text-sm" data-testid="client-detail-back">
          <ArrowLeft className="w-4 h-4" /> Retour à la liste des clients
        </button>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              {data?.client_account?.full_name || selected.name || selected.email}
              {livePremium && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-black bg-gradient-to-r from-yellow-400 to-primary px-2 py-0.5 rounded-full"><Crown className="w-3 h-3" /> PRO</span>}
              {liveTransformation && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-white bg-gradient-to-r from-purple-500 to-primary px-2 py-0.5 rounded-full"><Sparkles className="w-3 h-3" /> TRANSFORMATION</span>}
            </h2>
            <p className="text-xs text-zinc-500">{selected.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onGoToCoaching && onGoToCoaching(selected.email)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-zinc-300 border border-white/10 hover:border-primary/50 hover:text-white transition-colors"
              data-testid="client-goto-coaching"
            >
              <Dumbbell className="w-3.5 h-3.5" /> Voir le suivi coaching
            </button>
            <button
              onClick={() => setManualInvoiceModal({ customer_name: data?.client_account?.full_name || selected.name, customer_email: selected.email })}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-105 transition-transform"
              data-testid="client-new-invoice-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Nouvelle facture
            </button>
          </div>
        </div>

        {loadingDetail ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
        ) : (
          <>
            {/* Stats financières */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard label="Encaissé" value={`${(data?.stats?.lifetime_paid_chf || 0).toFixed(0)} CHF`} color="#10B981" />
              <StatCard label="En attente / programmé" value={`${(data?.stats?.lifetime_pending_chf || 0).toFixed(0)} CHF`} color="#FBBF24" />
              <StatCard label="Demandes calculateur" value={(calculations || []).length} color="#E91E63" />
              <StatCard label="Messages reçus" value={data?.stats?.contacts_count || 0} color="#8B5CF6" />
            </div>

            {/* Plan d'accès */}
            <PlanManager email={selected.email} csrfToken={csrfToken} subscription={data?.subscription} clientName={data?.client_account?.full_name || selected.name} onSaved={refetchProfile} />

            {/* Profil VIP / Tags / Notes */}
            {vipDraft && (
              <VipNotesEditor vipDraft={vipDraft} setVipDraft={setVipDraft} onSave={saveVip} dirty={vipDirty} saving={vipSaving} saveError={vipSaveError} />
            )}

            {/* Crédits IA / Coach / Plan alimentaire */}
            <CreditsEditor email={selected.email} csrfToken={csrfToken} />

            {/* Compte espace client */}
            {data?.client_account && (
              <SectionCard title="Compte espace client" icon={User}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {data.client_account.full_name && <Row label="Nom" value={data.client_account.full_name} />}
                  {data.client_account.phone && <Row label="Téléphone" icon={Phone} value={data.client_account.phone} />}
                  <Row label="Créé le" value={fmtDate(data.client_account.created_at)} />
                  {data.client_account.last_login_at && <Row label="Dernière connexion" value={fmtDate(data.client_account.last_login_at)} />}
                </div>
              </SectionCard>
            )}

            {/* Historique calculateur */}
            <Section title={<><Flame className="w-5 h-5 inline mr-2 text-primary" />Historique calculateur de calories ({(calculations || []).length})</>}>
              <PaymentsTable
                rows={calculations || []}
                columns={['Date', 'Objectif', 'Calories cible', 'Pro', 'Plan alimentaire']}
                renderRow={(c) => (
                  <tr key={c.calculation_id} className="border-t border-white/5 text-zinc-300" data-testid={`client-calc-row-${c.calculation_id}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-xs">{fmtDate(c.created_at)}</td>
                    <td className="px-4 py-3 text-xs">{c.goal || '—'}</td>
                    <td className="px-4 py-3 font-semibold text-white">{c.target_calories ? Math.round(c.target_calories) : '—'} kcal</td>
                    <td className="px-4 py-3">{c.has_pro ? <span className="text-green-400 text-xs inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Acheté</span> : <span className="text-zinc-500 text-xs">—</span>}</td>
                    <td className="px-4 py-3">{c.has_meal_plan ? <span className="text-green-400 text-xs inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Acheté</span> : <span className="text-zinc-500 text-xs">—</span>}</td>
                  </tr>
                )}
              />
            </Section>

            {/* Historique suppressions de programmes (discret, pour retrouver un programme supprimé par le client) */}
            <ProgramDeletionsSection email={selected.email} fmtDate={fmtDate} />

            {/* Recettes personnelles générées par l'IA (plans alimentaires) */}
            {personalRecipes && personalRecipes.length > 0 && (
              <Section title={<><ChefHat className="w-5 h-5 inline mr-2 text-emerald-400" />Recettes IA personnelles ({personalRecipes.length})</>}>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <label className="text-xs text-zinc-400">Catégorie pour promotion :</label>
                    <select value={promoteCategory} onChange={(e) => setPromoteCategory(e.target.value)}
                      className="bg-zinc-800 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white" data-testid="promote-category-select">
                      <option value="petit_dejeuner">Petit-déjeuner</option>
                      <option value="entree">Entrées</option>
                      <option value="plat_froid">Plats froids</option>
                      <option value="plat_chaud">Plats chauds</option>
                      <option value="dessert">Desserts</option>
                      <option value="collation">Collations</option>
                    </select>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {personalRecipes.map((r) => (
                      <div key={r.id} className="rounded-xl border border-white/10 bg-zinc-800/40 p-3 flex items-center gap-3" data-testid={`admin-personal-recipe-${r.id}`}>
                        {r.image_url ? <img src={r.image_url} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" /> : <div className="w-14 h-14 rounded-lg bg-zinc-700 flex items-center justify-center shrink-0"><ChefHat className="w-5 h-5 text-zinc-400" /></div>}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-white truncate">{r.name}</p>
                          <p className="text-xs text-zinc-500">{Math.round(r.macros.calories)} kcal · P {Math.round(r.macros.protein)}g</p>
                        </div>
                        <button onClick={() => promoteRecipe(r.id)} disabled={promoting === r.id}
                          className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold bg-primary/20 border border-primary/40 text-primary hover:bg-primary/30 transition-colors disabled:opacity-60"
                          data-testid={`promote-recipe-btn-${r.id}`}>
                          {promoting === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Ajouter à la bibliothèque'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </Section>
            )}

            {/* Factures virement */}
            <Section title={<><FileText className="w-5 h-5 inline mr-2 text-blue-400" />Factures virement bancaire ({(data?.invoices || []).length})</>}>
              <PaymentsTable
                rows={data?.invoices || []}
                columns={['N° facture', 'Date', 'Forfait', 'Montant', 'Statut', 'Action']}
                renderRow={(i) => (
                  <tr key={i.invoice_number} className="border-t border-white/5 text-zinc-300" data-testid={`client-invoice-row-${i.invoice_number}`}>
                    <td className="px-4 py-3 font-mono text-xs">{i.invoice_number}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs">{fmtDate(i.created_at)}</td>
                    <td className="px-4 py-3 text-xs max-w-[200px] truncate">
                      {i.items?.[0]?.description || '—'}
                      {i.installment_total > 1 && <div className="text-zinc-500">Mensualité {i.installment_number}/{i.installment_total}</div>}
                    </td>
                    <td className="px-4 py-3 font-semibold text-white">{(i.total || 0).toFixed(2)} CHF</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={i.status} />
                      {i.status === 'scheduled' && i.scheduled_date && <div className="text-zinc-500 text-[11px] mt-1">Prévue le {fmtDate(i.scheduled_date)}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {i.status === 'scheduled' ? (
                          <button onClick={() => sendScheduledNow(i.invoice_number)} className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-white text-xs rounded-lg font-semibold transition-colors inline-flex items-center gap-1" data-testid={`client-send-scheduled-${i.invoice_number}`}>
                            <Mail className="w-3.5 h-3.5" /> Envoyer maintenant
                          </button>
                        ) : i.status === 'pending' ? (
                          <button onClick={() => setConfirmModal({ kind: 'invoice', id: i.invoice_number, label: i.customer_name, amount: i.total })} className="px-3 py-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs rounded-lg font-semibold hover:scale-105 transition-transform inline-flex items-center gap-1" data-testid={`client-mark-paid-${i.invoice_number}`}>
                            <CheckCircle2 className="w-3.5 h-3.5" /> Marquer payée
                          </button>
                        ) : (
                          <span className="text-green-400 text-xs inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {fmtDate(i.paid_at)}</span>
                        )}
                        <a href={`${BACKEND_URL}/api/admin/billing/invoice/${i.invoice_number}/pdf`} target="_blank" rel="noreferrer" className="p-1.5 text-zinc-500 hover:text-primary transition-colors" data-testid={`client-download-invoice-${i.invoice_number}`} title="Télécharger le PDF">
                          <Download className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </td>
                  </tr>
                )}
              />
            </Section>

            {/* Sessions Stripe */}
            <Section title={<><CreditCard className="w-5 h-5 inline mr-2 text-violet-400" />Paiements Stripe ({(data?.sessions || []).length})</>}>
              <PaymentsTable
                rows={data?.sessions || []}
                columns={['Date', 'Forfait', 'Montant', 'Statut', 'Action']}
                renderRow={(s) => (
                  <tr key={s.session_id} className="border-t border-white/5 text-zinc-300" data-testid={`client-session-row-${s.session_id}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-xs">{fmtDate(s.created_at)}</td>
                    <td className="px-4 py-3 text-xs max-w-[200px] truncate">{s.description}</td>
                    <td className="px-4 py-3 font-semibold text-white">{(s.total_with_fees || 0).toFixed(2)} CHF</td>
                    <td className="px-4 py-3"><StatusBadge status={s.payment_status} /></td>
                    <td className="px-4 py-3">
                      {s.refunded ? (
                        <span className="text-zinc-500 text-xs inline-flex items-center gap-1"><Undo2 className="w-3.5 h-3.5" /> Remboursé</span>
                      ) : s.payment_status === 'paid' ? (
                        <button onClick={() => setRefundTarget(s)} className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/25 text-red-300 text-xs rounded-lg font-semibold transition-colors inline-flex items-center gap-1" data-testid={`client-refund-btn-${s.session_id}`}>
                            <Undo2 className="w-3.5 h-3.5" /> Rembourser
                          </button>
                      ) : <span className="text-zinc-500 text-xs">—</span>}
                    </td>
                  </tr>
                )}
              />
            </Section>

            {/* Messages reçus */}
            {data?.contacts?.length > 0 && (
              <SectionCard title={`Messages reçus (${data.contacts.length})`} icon={MessageSquare}>
                <div className="space-y-1.5">
                  {data.contacts.slice(0, 5).map((c, idx) => (
                    <div key={idx} className="p-2.5 bg-white/5 rounded-lg text-sm">
                      <div className="text-white font-semibold text-xs">{c.subject || '(sans sujet)'}</div>
                      <div className="text-zinc-400 text-xs mt-1">{c.message}</div>
                      <div className="text-zinc-600 text-[10px] mt-1">{fmtDate(c.created_at)}</div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Leads funnel */}
            {data?.leads?.length > 0 && (
              <SectionCard title={`Funnel / Leads (${data.leads.length})`} icon={Target}>
                <div className="flex flex-wrap gap-1.5">
                  {data.leads.map((l, idx) => (
                    <span key={idx} className="px-2 py-1 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-300 text-[11px]">
                      {l.source || 'lead'} · {fmtDate(l.created_at)}
                    </span>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Zone danger RGPD */}
            {data?.client_account?.user_id && (
              <DangerZone userId={data.client_account.user_id} email={selected.email} csrfToken={csrfToken} onDeleted={() => setSelected(null)} />
            )}
          </>
        )}

        {confirmModal && (
          <ConfirmMarkPaidModal modal={confirmModal} onCancel={() => setConfirmModal(null)} onConfirm={(note) => markPaid(confirmModal.kind, confirmModal.id, note)} loading={actionLoading} />
        )}
        {manualInvoiceModal && (
          <ManualInvoiceModal prefill={manualInvoiceModal} onClose={() => setManualInvoiceModal(null)} onCreated={() => { setManualInvoiceModal(null); refetchProfile(); }} fetchWithCsrf={fetchWithCsrf} />
        )}
        {refundTarget && (
          <div className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !refunding && setRefundTarget(null)}>
            <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="refund-modal">
              <h4 className="flex items-center gap-2 font-bold text-white mb-2"><AlertTriangle className="w-4 h-4 text-red-400" /> Confirmer le remboursement</h4>
              <p className="text-zinc-400 text-sm mb-4">Remboursement intégral de <strong className="text-white">{(refundTarget.total_with_fees || 0).toFixed(2)} CHF</strong> via Stripe. Cette action est irréversible.</p>
              <div className="flex gap-2">
                <button onClick={() => setRefundTarget(null)} className="flex-1 px-3 py-2 rounded-lg text-sm font-semibold text-zinc-400 bg-white/5 hover:bg-white/10" data-testid="refund-cancel-btn">Annuler</button>
                <button onClick={doRefund} disabled={refunding} className="flex-1 px-3 py-2 rounded-lg text-sm font-bold text-white bg-red-600 hover:bg-red-500 disabled:opacity-50 inline-flex items-center justify-center gap-1.5" data-testid="refund-confirm-btn">
                  {refunding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />} Rembourser
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ----------------------------- Liste des clients -----------------------------
  return (
    <div data-testid="admin-clients-panel" className="space-y-4">
      {emailFailures.length > 0 && (
        <div className="rounded-2xl border border-rose-500/25 bg-rose-500/5 p-4 space-y-2" data-testid="email-failures-banner">
          <h4 className="flex items-center gap-2 font-bold text-rose-300 text-sm">
            <MailX className="w-4 h-4" /> {emailFailures.length} email{emailFailures.length > 1 ? 's' : ''} critique{emailFailures.length > 1 ? 's' : ''} non envoyé{emailFailures.length > 1 ? 's' : ''}
          </h4>
          <p className="text-zinc-500 text-xs">
            Email de bienvenue non reçu par un client activé, ou alerte "carte refusée" non transmise à un abonné (ex: panne SMTP). Renvoyez-le manuellement dès que le problème est résolu.
          </p>
          <div className="space-y-1.5">
            {emailFailures.map((f) => (
              <div key={`${f.to_email}-${f.context}-${f.failed_at}`} className="flex items-center justify-between gap-3 bg-white/5 rounded-lg px-3 py-2" data-testid={`email-failure-row-${f.to_email}`}>
                <div className="text-xs min-w-0">
                  <div className="text-white font-semibold truncate">
                    {f.to_email}{f.plan ? ` · ${f.plan}` : ''}
                    <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold ${f.context === 'payment_failed_client' ? 'bg-red-500/20 text-red-300' : 'bg-blue-500/20 text-blue-300'}`}>
                      {f.context === 'payment_failed_client' ? 'Carte refusée' : 'Bienvenue'}
                    </span>
                    {f.error_type === 'invalid_recipient' ? (
                      <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-zinc-500/20 text-zinc-300" data-testid={`email-error-type-${f.to_email}`}>Adresse invalide</span>
                    ) : f.error_type === 'server_issue' ? (
                      <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-300" data-testid={`email-error-type-${f.to_email}`}>Panne serveur (transitoire)</span>
                    ) : f.error_type === 'frontend_not_whitelisted' ? (
                      <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-500/20 text-orange-300" title="Secret FRONTEND_URL mal configuré en production — pas un souci SMTP" data-testid={`email-error-type-${f.to_email}`}>URL non autorisée (secret)</span>
                    ) : null}
                  </div>
                  <div className="text-zinc-500 truncate">{f.error}</div>
                </div>
                <button
                  onClick={() => previewFailedEmail(f)}
                  disabled={previewingEmail === f.to_email}
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/15 text-zinc-300 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50"
                  data-testid={`preview-email-btn-${f.to_email}`}
                >
                  {previewingEmail === f.to_email ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Aperçu
                </button>
                <button
                  onClick={() => resendFailedEmail(f)}
                  disabled={resendingEmail === f.to_email}
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/25 text-rose-300 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50"
                  data-testid={`resend-email-btn-${f.to_email}`}
                >
                  {resendingEmail === f.to_email ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Renvoyer
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {emailPreview && <EmailPreviewModal html={emailPreview.html} subject={emailPreview.subject} onClose={() => setEmailPreview(null)} />}
      <div className="rounded-2xl border border-white/10 bg-white/5" data-testid="reminders-history-section">
        <div className="w-full flex items-center justify-between gap-3 px-4 py-3">
          <button onClick={() => setShowReminders((s) => !s)} className="flex-1 flex items-center justify-between gap-3" data-testid="reminders-history-toggle">
            <h4 className="flex items-center gap-2 font-bold text-white text-sm">
              <Bell className="w-4 h-4 text-primary" /> Historique des rappels ({remindersHistory.length})
            </h4>
            <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${showReminders ? 'rotate-180' : ''}`} />
          </button>
          <a
            href={`${BACKEND_URL}/api/admin/reminders-history/export.csv`}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-zinc-300 bg-white/5 border border-white/10 hover:border-primary/40 hover:text-white transition-colors"
            data-testid="export-reminders-csv-btn"
            title="Télécharger l'historique complet des rappels au format CSV"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </a>
        </div>
        {showReminders && (
          <div className="px-4 pb-4 space-y-1.5 max-h-72 overflow-y-auto" data-testid="reminders-history-list">
            {remindersHistory.length === 0 ? (
              <p className="text-zinc-500 text-xs">Aucun rappel envoyé pour le moment.</p>
            ) : remindersHistory.map((r, idx) => {
              const [label, cls] = REMINDER_LABEL[r.type] || [r.type, 'bg-zinc-500/20 text-zinc-300'];
              return (
                <div key={`${r.email}-${r.type}-${idx}`} className="flex items-center justify-between gap-3 bg-white/5 rounded-lg px-3 py-2 text-xs" data-testid={`reminder-row-${idx}`}>
                  <span className="text-white truncate">{r.email}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${cls}`}>{label}</span>
                    <span className="text-zinc-500">{fmtDate(r.sent_at)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative max-w-xs flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un client ou un prospect…" className="w-full bg-zinc-800 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-white text-sm focus:border-primary focus:outline-none" data-testid="admin-clients-search" />
        </div>
        <button
          onClick={() => setTestFilterOnly((v) => !v)}
          className={`inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold border transition-colors ${testFilterOnly ? 'bg-amber-500/20 border-amber-500/40 text-amber-300' : 'bg-white/5 border-white/10 text-zinc-400 hover:text-white'}`}
          data-testid="test-accounts-filter-toggle"
          title="Filtrer les comptes avec un pattern d'email suspect (@example.com, @test.com…)"
        >
          <AlertTriangle className="w-3.5 h-3.5" /> Comptes tests ({testAccountsCount})
        </button>
        <button
          onClick={openTrash}
          className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold text-zinc-300 bg-white/5 border border-white/10 hover:border-primary/40 hover:text-white transition-colors"
          data-testid="open-trash-btn"
        >
          <Trash2 className="w-3.5 h-3.5" /> Corbeille
        </button>
        <a
          href={`${BACKEND_URL}/api/admin/clients/export.csv`}
          className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold text-zinc-300 bg-white/5 border border-white/10 hover:border-primary/40 hover:text-white transition-colors"
          data-testid="export-clients-csv-btn"
          title="Exporter la liste complète des clients (email, plan, statut) en CSV"
        >
          <Download className="w-3.5 h-3.5" /> Export CSV
        </a>
      </div>
      <div className="bg-zinc-900 border border-white/10 rounded-2xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-clients-table">
          <thead className="bg-zinc-800/70 text-zinc-400">
            <tr>{['Client', 'Email', 'Plan', 'Factures en attente', 'Total payé', ''].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {clients === null ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center"><Loader2 className="w-6 h-6 text-primary animate-spin mx-auto" /></td></tr>
            ) : filtered.length === 0 && prospects.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-500">{searchingProspects ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Aucun client'}</td></tr>
            ) : filtered.map((c, i) => (
              <tr key={i} className="border-t border-white/5 text-zinc-300 hover:bg-zinc-800/40" data-testid={`admin-client-row-${c.email}`}>
                <td className="px-4 py-3 whitespace-nowrap text-white">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-zinc-500" /> {c.name || '—'}
                    {isTestEmail(c.email) && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/15 text-amber-300 border border-amber-500/25" data-testid={`test-badge-${c.email}`}>TEST ?</span>}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">{c.email}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    {c.premium ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-black bg-gradient-to-r from-yellow-400 to-primary px-2 py-0.5 rounded-full"><Crown className="w-3 h-3" /> PRO</span> : <span className="text-zinc-500 text-xs">Gratuit</span>}
                    {c.transformation && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-white bg-gradient-to-r from-purple-500 to-primary px-2 py-0.5 rounded-full"><Sparkles className="w-3 h-3" /></span>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {c.pending_invoices > 0 ? (
                    <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-yellow-500/20 text-yellow-400 text-xs font-bold" data-testid={`client-pending-badge-${c.email}`}>{c.pending_invoices}</span>
                  ) : <span className="text-zinc-500 text-xs">0</span>}
                </td>
                <td className="px-4 py-3 font-semibold text-white whitespace-nowrap">{(c.total_paid_chf || 0).toFixed(0)} CHF</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => openClient(c)} className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-105 transition-transform" data-testid={`client-view-btn-${c.email}`}>Voir la fiche</button>
                    {c.user_id && (
                      <button
                        onClick={() => setQuickDeleteTarget(c)}
                        className="p-1.5 rounded-lg text-red-300 bg-red-500/10 hover:bg-red-500/20 border border-red-500/25 transition-colors"
                        title="Supprimer ce compte"
                        data-testid={`client-quick-delete-btn-${c.email}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {prospects.map((p) => (
              <tr key={p.email} className="border-t border-white/5 text-zinc-400 bg-amber-500/[0.03]" data-testid={`prospect-row-${p.email}`}>
                <td className="px-4 py-3 whitespace-nowrap"><div className="flex items-center gap-2"><User className="w-4 h-4 text-amber-500/60" /> {p.name || '—'}</div></td>
                <td className="px-4 py-3 whitespace-nowrap">{p.email}</td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 bg-amber-500/15 text-amber-300 text-[10px] rounded-full font-semibold">Prospect (sans compte)</span></td>
                <td className="px-4 py-3 text-xs">—</td>
                <td className="px-4 py-3 font-semibold text-white whitespace-nowrap">{(p.lifetime_value_chf || 0).toFixed(0)} CHF</td>
                <td className="px-4 py-3">
                  <button onClick={() => openClient({ email: p.email, name: p.name })} className="px-3 py-1.5 rounded-lg text-xs font-bold text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/25" data-testid={`prospect-view-btn-${p.email}`}>Voir la fiche</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {quickDeleteTarget && (
        <div className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !quickDeleting && setQuickDeleteTarget(null)}>
          <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="quick-delete-modal">
            <h4 className="flex items-center gap-2 font-bold text-white mb-2"><Trash2 className="w-4 h-4 text-amber-400" /> Envoyer en corbeille ?</h4>
            <p className="text-zinc-400 text-sm mb-4">
              Le compte <strong className="text-white">{quickDeleteTarget.email}</strong> disparaîtra de la liste et ne pourra plus se connecter, mais reste <strong className="text-amber-300">récupérable pendant 30 jours</strong> depuis la Corbeille avant suppression définitive.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setQuickDeleteTarget(null)} disabled={quickDeleting} className="flex-1 px-3 py-2 rounded-lg text-sm font-semibold text-zinc-400 bg-white/5 hover:bg-white/10 disabled:opacity-50" data-testid="quick-delete-cancel-btn">Annuler</button>
              <button onClick={doQuickDelete} disabled={quickDeleting} className="flex-1 px-3 py-2 rounded-lg text-sm font-bold text-white bg-amber-600 hover:bg-amber-500 disabled:opacity-50 inline-flex items-center justify-center gap-1.5" data-testid="quick-delete-confirm-btn">
                {quickDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Envoyer en corbeille
              </button>
            </div>
          </div>
        </div>
      )}
      {showTrash && (
        <div className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowTrash(false)}>
          <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5 max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="trash-modal">
            <div className="flex items-center justify-between mb-3">
              <h4 className="flex items-center gap-2 font-bold text-white"><Trash2 className="w-4 h-4 text-amber-400" /> Corbeille — comptes récupérables 30 jours</h4>
              <button onClick={() => setShowTrash(false)} className="text-zinc-500 hover:text-white" data-testid="trash-close-btn"><X className="w-4 h-4" /></button>
            </div>
            {trash === null ? (
              <div className="py-8 text-center"><Loader2 className="w-6 h-6 text-primary animate-spin mx-auto" /></div>
            ) : trash.length === 0 ? (
              <p className="text-zinc-500 text-sm py-6 text-center" data-testid="trash-empty">Corbeille vide.</p>
            ) : (
              <div className="space-y-2">
                {trash.map((t) => (
                  <div key={t.user_id} className="flex items-center justify-between gap-3 bg-white/5 rounded-lg px-3 py-2.5" data-testid={`trash-row-${t.email}`}>
                    <div className="min-w-0">
                      <p className="text-white text-sm font-semibold truncate">{t.name || t.email}</p>
                      <p className="text-zinc-500 text-xs truncate">{t.email} · supprimé le {fmtDate(t.deleted_at)} · <span className={t.days_remaining <= 3 ? 'text-red-400 font-semibold' : ''}>{t.days_remaining}j restant{t.days_remaining > 1 ? 's' : ''}</span></p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => restoreFromTrash(t)} disabled={trashBusyId === t.user_id} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/25 disabled:opacity-50" data-testid={`trash-restore-btn-${t.email}`}>
                        {trashBusyId === t.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />} Restaurer
                      </button>
                      <button onClick={() => purgeFromTrash(t)} disabled={trashBusyId === t.user_id} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-red-300 bg-red-500/10 hover:bg-red-500/20 border border-red-500/25 disabled:opacity-50" data-testid={`trash-purge-btn-${t.email}`}>
                        <Trash2 className="w-3.5 h-3.5" /> Supprimer maintenant
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminClientsPanel;
